import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSlides();
  }, []);

  useEffect(() => {
    if (slides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slides.length]);

  const fetchSlides = async () => {
    try {
      const response = await axios.get(`${API}/hero-slides`);
      // Convert relative URLs to full URLs
      const slidesWithFullUrls = response.data.map(slide => {
        const imageUrl = slide.image?.startsWith('http') ? slide.image : `${BACKEND_URL}${slide.image}`;
        console.log('Hero slide image:', slide.image, '→', imageUrl);
        return {
          ...slide,
          image: imageUrl
        };
      });
      setSlides(slidesWithFullUrls);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="h-screen w-full bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">No slides available</div>
      </div>
    );
  }

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsAnimating(false), 800);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setTimeout(() => setIsAnimating(false), 800);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating && index !== currentSlide) {
      setIsAnimating(true);
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 800);
    }
  };

  const scrollToSpaces = () => {
    document.getElementById('our-spaces')?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderHeadline = (headline, highlightWord) => {
    if (!highlightWord) {
      return <span>{headline}</span>;
    }

    const parts = headline.split(highlightWord);
    return (
      <>
        {parts[0]}
        <span className="relative inline-block px-4">
          <span className="relative z-10 text-white">{highlightWord}</span>
          <span className="absolute inset-0 bg-[#E12429] transform -skew-x-12"></span>
        </span>
        {parts[1]}
      </>
    );
  };

  return (
    <div className="relative h-[70vh] sm:h-[80vh] md:h-screen w-full overflow-hidden" data-testid="hero-slider">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background Image with Continuous Zoom Animation */}
          <div className="absolute inset-0 animate-ken-burns">
            <img 
              src={slide.image}
              alt={slide.headline}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center center' }}
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          {/* Content */}
          <div className="relative h-full flex items-center justify-center text-center px-4 sm:px-6">
            <div
              className={`max-w-4xl w-full transition-all duration-1000 delay-200 ${
                index === currentSlide
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
                {renderHeadline(slide.headline, slide.highlight_word)}
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
                {slide.subheading}
              </p>
              <button
                onClick={scrollToSpaces}
                data-testid="hero-cta-button"
                className="btn-primary inline-block text-sm sm:text-base"
              >
                {slide.cta}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows - Hidden on mobile, visible on tablet and up */}
      <button
        onClick={prevSlide}
        data-testid="hero-prev-button"
        className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all z-10"
      >
        <ChevronLeft className="text-white" size={24} />
      </button>
      <button
        onClick={nextSlide}
        data-testid="hero-next-button"
        className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all z-10"
      >
        <ChevronRight className="text-white" size={24} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            data-testid={`hero-dot-${index}`}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide
                ? 'bg-[#E12429] w-8'
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
}